import { SampleModel } from "@/types/models";
import { privateApi } from "./api";
import { getToken } from "./auth.service";
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
const getSamples = async (projectId: string) => {
    return await privateApi.get<SampleModel[]>(`/project/${projectId}/sample`);
};

const getSample = async ({ projectId, sampleId }: { projectId: string; sampleId: string }) => {
    return await privateApi.get<SampleModel>(`/project/${projectId}/sample/${sampleId}`);
};

export type SampleGraphs = {
    date_utc: string[];
    series: Record<string, number[]>;
};

const getSampleGraphs = async ({ projectId, sampleId }: { projectId: string; sampleId: string }) => {
    const { data } = await privateApi.get<SampleGraphs | null>(`/project/${projectId}/sample/${sampleId}/graphs`);
    // Nest answers a null body as an empty string.
    return data || null;
};

const updateSample = async ({ 
    projectId, 
    sampleId, 
    data 
}: { 
    projectId: string; 
    sampleId: string; 
    data: { 
        name?: string; 
        description?: string; 
        topEdgeDepthCm?: number;
        tiltDeg?: number;
        latitude?: number | null;
        longitude?: number | null;
    } 
}) => {
    return await privateApi.patch<SampleModel>(`/project/${projectId}/sample/${sampleId}`, data);
};

const deleteSample = async ({ projectId, sampleId }: { projectId: string; sampleId: string }) => {
    await privateApi.delete(`/project/${projectId}/sample/${sampleId}`);
};

// Hooks
export const useSamples = (projectId?: string) => {
    return useQuery({
        queryKey: ['samples', projectId],
        queryFn: () => {
            if (!projectId) return Promise.reject("Project ID missing");
            return getSamples(projectId).then((response) => response.data);
        },
        enabled: !!projectId,
    });
};

export const useSample = (projectId?: string, sampleId?: string) => {
    return useQuery({
        queryKey: ['sample', projectId, sampleId],
        queryFn: () => {
            if (!projectId || !sampleId) return Promise.reject("Project ID or Sample ID missing");
            return getSample({ projectId, sampleId }).then((response) => response.data);
        },
        enabled: !!projectId && !!sampleId,
    });
};

export const useSampleGraphs = (projectId?: string, sampleId?: string) => {
    return useQuery({
        queryKey: ['sampleGraphs', projectId, sampleId],
        queryFn: () => {
            if (!projectId || !sampleId) return Promise.reject("Project ID or Sample ID missing");
            return getSampleGraphs({ projectId, sampleId });
        },
        enabled: !!projectId && !!sampleId,
    });
};

export const useUpdateSample = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: updateSample,
        onSuccess: (_, { projectId, sampleId }) => {
            queryClient.invalidateQueries({ queryKey: ['samples', projectId] });
            queryClient.invalidateQueries({ queryKey: ['sample', projectId, sampleId] });
            queryClient.invalidateQueries({ queryKey: ['field-data', projectId] });
        },
        onError: (error) => {
            console.error("Error while updating sample", error);
            throw error;
        },
    });
};

export const useDeleteSample = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: deleteSample,
        onSuccess: (_, { projectId, sampleId }) => {
            queryClient.invalidateQueries({ queryKey: ['samples', projectId] });
            queryClient.invalidateQueries({ queryKey: ['sample', projectId, sampleId] });
            queryClient.invalidateQueries({ queryKey: ['field-data', projectId] });
        },
        onError: (error) => {
            console.error("Error while deleting sample", error);
            throw error;
        },
    });
};

/** A row of the Sample table, as the backoffice's Samples page lists it. */
export type BackOfficeSample = {
    id: string;
    name: string;
    description: string | null;
    projectId: string;
    projectName: string;
    scannerSerialNumber: string | null;
    softDelete: boolean;
    monitoring: 'FIELD' | 'BENCH' | null;
    rotateClockwise: boolean;
    topEdgeDepthCm: number;
    tiltDeg: number;
    latitude: number | null;
    longitude: number | null;
    imageCount: number;
    createdAt: string;
    updatedAt: string;
};

const getBackOfficeSamples = async () => {
    const { data } = await privateApi.get<BackOfficeSample[]>('/sample');
    return data;
};

const setSampleRotation = async ({
    sampleId,
    rotateClockwise,
}: {
    sampleId: string;
    rotateClockwise: boolean;
}) => {
    await privateApi.patch(`/sample/${sampleId}/rotation`, { rotateClockwise });
};

export const useBackOfficeSamples = () => {
    return useQuery({
        queryKey: ['backOfficeSamples'],
        queryFn: getBackOfficeSamples,
        enabled: !!getToken(),
    });
};

export const useSetSampleRotation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: setSampleRotation,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['backOfficeSamples'] });
        },
        onError: (error) => {
            console.error('Error while updating sample rotation', error);
        },
    });
};
// Backoffice "Hide" list: samples whose analyses are reserved to super admins.
export type HiddenSample = {
    sampleId: string;
    name: string;
    projectId: string;
    projectName: string;
    hideRoots: boolean;
    hideFauna: boolean;
};

const getHiddenSamples = async () => {
    const { data } = await privateApi.get<HiddenSample[]>('/sample/hide');
    return data;
};

const addHiddenSample = async (body: { projectId: string; sampleId: string }) => {
    await privateApi.post('/sample/hide', body);
};

const setHiddenSample = async ({
    sampleId,
    ...flags
}: {
    sampleId: string;
    hideRoots?: boolean;
    hideFauna?: boolean;
}) => {
    await privateApi.patch(`/sample/${sampleId}/hide`, flags);
};

const removeHiddenSample = async (sampleId: string) => {
    await privateApi.delete(`/sample/${sampleId}/hide`);
};

export const useHiddenSamples = () => {
    return useQuery({
        queryKey: ['hiddenSamples'],
        queryFn: getHiddenSamples,
        enabled: !!getToken(),
    });
};

const useHiddenSamplesMutation = <T,>(mutationFn: (arg: T) => Promise<void>) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['hiddenSamples'] });
        },
    });
};

export const useAddHiddenSample = () => useHiddenSamplesMutation(addHiddenSample);
export const useSetHiddenSample = () => useHiddenSamplesMutation(setHiddenSample);
export const useRemoveHiddenSample = () =>
    useHiddenSamplesMutation(removeHiddenSample);
